import { Injectable, signal, computed, inject } from '@angular/core';
import { EntityApiService } from './entity-api.service';
import {
  Organization,
  OrganizationModel,
  CreateOrganization,
  UpdateOrganization
} from '../models/organization.model';
import {Observable, tap, map, filter, first} from 'rxjs';
import { ToastService } from './toast.service';
import {toObservable} from '@angular/core/rxjs-interop';
import {LoggerService} from './logger.service';

/** LocalStorage key for persisting active organization ID */
const ACTIVE_ORG_STORAGE_KEY = 'sysprompthub:activeOrgId';

/**
 * Service for managing organizations and organization context.
 *
 * Handles:
 * - Loading and caching user's organizations
 * - Managing active organization state (which org is currently being viewed)
 * - Persisting active org to localStorage for session continuity
 * - Providing computed values for common organization queries
 *
 * The active organization determines which prompts/packs are shown and
 * what permissions the user has for creating/editing content.
 *
 * @example
 * ```typescript
 * // Load organizations on app init
 * orgService.loadOrganizations().subscribe();
 *
 * // Get active org
 * const activeOrg = orgService.activeOrganization();
 *
 * // Switch organization
 * orgService.setActiveOrganization(org);
 *
 * // Check permissions
 * const canCreate = orgService.canCreateInActiveOrg();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationService extends EntityApiService<Organization, OrganizationModel, CreateOrganization> {
  private readonly toastService = inject(ToastService);
  private readonly logger = inject(LoggerService).get('org-service')

  /** Private writable signal for active organization */
  private readonly _activeOrganization = signal<OrganizationModel | null>(null);

  private readonly activeOrganizationObservable = toObservable(this._activeOrganization)
    .pipe(filter(org => org !== null));

  /** Private writable signal for all user's organizations */
  private readonly _organizations = signal<OrganizationModel[]>([]);

  /** Public readonly signal for active organization */
  readonly activeOrganization = this._activeOrganization.asReadonly();


  /** Public readonly signal for all user's organizations */
  readonly organizations = this._organizations.asReadonly();

  /** Computed signal returning the user's personal organization */
  readonly personalOrganization = computed(() =>
    this._organizations().find(org => org.isPersonal) ?? null
  );

  /** Computed signal returning all team (non-personal) organizations */
  readonly teamOrganizations = computed(() =>
    this._organizations().filter(org => !org.isPersonal)
  );

  /** Computed signal checking if user can create content in active org */
  readonly canCreateInActiveOrg = computed(() =>
    this._activeOrganization()?.canCreateContent ?? false
  );

  constructor() {
    super(OrganizationModel, 'organizations');
  }

  /**
   * Load all organizations for the current user from the API.
   *
   * This method:
   * 1. Fetches organizations from /organizations endpoint
   * 2. Attempts to restore previously active org from localStorage
   * 3. Validates that saved org is still valid (user is still a member)
   * 4. Falls back to personal org (first in list) if saved org is invalid
   * 5. Updates reactive signals with loaded data
   *
   * Should be called once when user authenticates (handled by AuthService).
   *
   * @returns Observable of organization models
   */
  loadOrganizations(): Observable<OrganizationModel[]> {
    // Organizations endpoint returns array, not PagedList
    // Use http directly but construct proper URL via apiUrlFor
    return this.http.get<Organization[]>(
      `${this.apiBaseUrl}${this.apiUrlFor()}`
    ).pipe(
      map(orgs => orgs.map(org => new OrganizationModel(org))),
      tap(orgs => {
        this._organizations.set(orgs);
        this.logger.debug('Organizations loaded');

        // Don't override if active org already set (e.g., from navigation)
        if (this._activeOrganization()) return;

        // Try to restore saved active org from localStorage
        const savedOrgId = this.getSavedActiveOrgId();
        let activeOrg: OrganizationModel | null = null;

        if (savedOrgId) {
          // Check if saved org is still valid (user still member)
          activeOrg = orgs.find(org => org.id === savedOrgId) ?? null;
        }

        // Fallback to personal org (first in list per backend spec)
        if (!activeOrg && orgs.length > 0) {
          activeOrg = orgs[0];
        }

        if (activeOrg) {
          this._activeOrganization.set(activeOrg);
          this.saveActiveOrgId(activeOrg.id);
        }
      })
    );
  }

  /**
   * Get saved active organization ID from localStorage.
   * Returns null if not found or on storage error.
   * @private
   */
  private getSavedActiveOrgId(): string | null {
    try {
      return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to read active org from localStorage:', e);
      return null;
    }
  }

  /**
   * Save active organization ID to localStorage for session continuity.
   * Fails silently on storage errors (e.g., incognito mode, quota exceeded).
   * @private
   */
  private saveActiveOrgId(orgId: string): void {
    try {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
    } catch (e) {
      console.warn('Failed to save active org to localStorage:', e);
    }
  }

  /**
   * Clear saved active organization ID from localStorage.
   * Called when active org is deleted.
   * @private
   */
  private clearSavedActiveOrgId(): void {
    try {
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear active org from localStorage:', e);
    }
  }

  /**
   * Create a new organization and add it to local state.
   * Overrides base create to update the organizations signal.
   *
   * @param request - Organization creation data (name, description)
   * @returns Observable of created organization model
   */
  override create(request: CreateOrganization): Observable<OrganizationModel>;
  override create(scope: never, request: never): Observable<OrganizationModel>;
  override create(request: CreateOrganization): Observable<OrganizationModel> {
    return super.create(request).pipe(
      tap(org => {
        // Add new org to local cache
        this._organizations.update(orgs => [...orgs, org]);
      })
    );
  }

  /**
   * Update an existing organization and sync local state.
   * If the active organization is updated, refreshes active org reference.
   *
   * @param data - Update data including organization ID
   * @returns Observable of updated organization model
   */
  override update(data: UpdateOrganization): Observable<OrganizationModel> {
    return super.update(data).pipe(
      tap(updatedOrg => {
        // Update in organizations list
        this._organizations.update(orgs =>
          orgs.map(org => org.id === updatedOrg.id ? updatedOrg : org)
        );
        // Update active org if it's the one being updated
        if (this._activeOrganization()?.id === updatedOrg.id) {
          this._activeOrganization.set(updatedOrg);
          // Update saved ID in case name changed (though ID doesn't change)
          this.saveActiveOrgId(updatedOrg.id);
        }
      })
    );
  }

  /**
   * Delete an organization and remove from local state.
   * If the deleted org was active, switches to personal org.
   *
   * @param id - Organization ID to delete
   * @returns Observable indicating success
   */
  override delete(id: string): Observable<boolean> {
    return super.delete(id).pipe(
      tap(() => {
        // Remove from organizations list
        this._organizations.update(orgs =>
          orgs.filter(org => org.id !== id)
        );
        // If active org was deleted, switch to personal org
        if (this._activeOrganization()?.id === id) {
          const personalOrg = this.personalOrganization();
          this._activeOrganization.set(personalOrg);
          if (personalOrg) {
            this.saveActiveOrgId(personalOrg.id);
          } else {
            this.clearSavedActiveOrgId();
          }
        }
      })
    );
  }

  /**
   * Set the active organization for the user session.
   *
   * This method:
   * 1. Accepts either an OrganizationModel or organization ID string
   * 2. Updates the active organization signal
   * 3. Persists the selection to localStorage
   * 4. Shows a toast notification (only if switching from another org, not on initial load)
   *
   * Used when user switches organizations via the UI.
   *
   * @param organization - Organization model or organization ID
   */
  setActiveOrganization(organization: OrganizationModel | string) {
    let org: OrganizationModel | undefined;

    if (typeof organization === 'string') {
      // Look up org by ID in cached organizations
      org = this._organizations().find(o => o.id === organization);
    } else {
      org = organization;
    }

    if (org) {
      const previousOrg = this._activeOrganization();
      this._activeOrganization.set(org);
      this.saveActiveOrgId(org.id);

      // Show toast notification if org actually changed (not initial load)
      if (previousOrg && previousOrg.id !== org.id) {
        this.showOrgSwitchToast(org);
      }
    }
  }

  /**
   * Show toast notification when user switches organizations.
   * Displays different message for personal vs team organizations.
   * @private
   */
  private showOrgSwitchToast(org: OrganizationModel): void {
    const message = org.isPersonal
      ? `Switched to personal workspace`
      : `Switched to ${org.name}`;

    this.toastService.info(message);
  }

  getActiveOrg(): Observable<OrganizationModel> {
    return this.activeOrganizationObservable.pipe(first())
  }

  /**
   * Get active organization ID for API calls.
   * Returns null if no organization is active yet.
   *
   * @returns Organization ID or null
   */
  getActiveOrgId(): Observable<string> {
    return this.getActiveOrg().pipe(map(org => org.id));
  }

  /**
   * Get the active organization name for display purposes only.
   * Use getActiveOrgId() for API calls.
   *
   * @returns Organization name or null
   */
  getActiveOrgName(): Observable<string> {
    return this.getActiveOrg().pipe(map(org => org.name))
  }
}
